import { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Fab,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { PayrollDTO, AddPayrollInput } from '@school-homestead/shared'

const EMPTY_FORM: AddPayrollInput = {
  teacherName: '',
  hoursWorked: 0,
  hourlyRate: 0,
}

export default function PayrollPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<AddPayrollInput>(EMPTY_FORM)

  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll'],
    queryFn: async () => {
      const res = await api.get<PayrollDTO[]>('/api/payroll')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
  })

  const addMutation = useMutation({
    mutationFn: async (input: AddPayrollInput) => {
      const res = await api.post<PayrollDTO>('/api/payroll', input)
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      setOpen(false)
      setForm(EMPTY_FORM)
    },
  })

  function handleClose() {
    setOpen(false)
    setForm(EMPTY_FORM)
    addMutation.reset()
  }

  const grossPreview =
    form.hoursWorked > 0 && form.hourlyRate > 0
      ? (form.hoursWorked * form.hourlyRate).toFixed(2)
      : null

  const isFormValid =
    form.teacherName.trim().length > 0 && form.hoursWorked > 0 && form.hourlyRate > 0

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Payroll
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          Add Payroll
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load payroll records.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ p: 2 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ border: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date Paid</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Teacher</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Hours
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Rate / hr
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Gross Pay
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                        No payroll records yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (data ?? []).map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>
                          {new Date(record.datePaid).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{record.teacherName}</TableCell>
                        <TableCell align="right">{record.hoursWorked}</TableCell>
                        <TableCell align="right">${record.hourlyRate.toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          ${record.grossPay.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Mobile FAB */}
      <Tooltip title="Add Payroll">
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, display: { xs: 'flex', sm: 'none' } }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Add Payroll Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Add Payroll Record</DialogTitle>
        <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Teacher Name"
            value={form.teacherName}
            onChange={(e) => setForm({ ...form, teacherName: e.target.value })}
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
          <TextField
            label="Hours Worked"
            type="number"
            value={form.hoursWorked > 0 ? form.hoursWorked : ''}
            onChange={(e) => setForm({ ...form, hoursWorked: parseFloat(e.target.value) || 0 })}
            inputProps={{ min: 0, step: '0.5' }}
            fullWidth
          />
          <TextField
            label="Hourly Rate ($)"
            type="number"
            value={form.hourlyRate > 0 ? form.hourlyRate : ''}
            onChange={(e) => setForm({ ...form, hourlyRate: parseFloat(e.target.value) || 0 })}
            inputProps={{ min: 0, step: '0.01' }}
            fullWidth
          />

          {grossPreview && (
            <Box
              sx={{
                bgcolor: 'success.light',
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="success.dark" fontWeight={500}>
                Gross Pay Preview
              </Typography>
              <Typography variant="h5" fontWeight={700} color="success.dark">
                ${grossPreview}
              </Typography>
            </Box>
          )}

          {addMutation.isError && (
            <Alert severity="error">
              {(addMutation.error as Error).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={addMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => addMutation.mutate(form)}
            disabled={!isFormValid || addMutation.isPending}
            loading={addMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
